/**
 * Wishlist — saved product handles in localStorage ("cn-wishlist").
 * - [data-wishlist-toggle][data-handle] buttons toggle a piece (aria-pressed reflects state).
 * - [data-wishlist-count] badges show the count.
 * - #wishlist-drawer lists saved pieces, fetched from /products/{handle}.js.
 */
const KEY = 'cn-wishlist';
const script = document.querySelector('script[data-wishlist-script]');
const currency = script?.dataset.currency || 'USD';
const locale = script?.dataset.locale || document.documentElement.lang || 'en';
const labelAdd = script?.dataset.labelAdd || 'Save __TITLE__';
const labelRemove = script?.dataset.labelRemove || 'Remove __TITLE__';
const money = new Intl.NumberFormat(locale, { style: 'currency', currency });
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const read = () => {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
};
const write = (list) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, 50)));
  } catch {}
};

function syncButtons() {
  const saved = new Set(read());
  document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
    const on = saved.has(btn.dataset.handle);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    const title = btn.dataset.title || '';
    btn.setAttribute('aria-label', (on ? labelRemove : labelAdd).replace('__TITLE__', title));
  });
  document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
    badge.textContent = String(saved.size);
    badge.hidden = saved.size === 0;
  });
}

function toggle(handle) {
  const list = read();
  const i = list.indexOf(handle);
  if (i === -1) list.unshift(handle);
  else list.splice(i, 1);
  write(list);
  syncButtons();
  renderDrawer();
  renderPage();
}

const cache = new Map();
async function fetchProduct(handle) {
  if (cache.has(handle)) return cache.get(handle);
  const p = fetch(`${root}products/${encodeURIComponent(handle)}.js`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  cache.set(handle, p);
  return p;
}

async function renderDrawer() {
  const drawer = document.getElementById('wishlist-drawer');
  const list = drawer?.querySelector('[data-wishlist-list]');
  const empty = drawer?.querySelector('[data-wishlist-empty]');
  const tpl = document.getElementById('wishlist-item-template');
  if (!drawer || !list || !tpl) return;
  const handles = read();
  empty.hidden = handles.length > 0;
  if (!drawer.open) return;
  const products = await Promise.all(handles.map(fetchProduct));
  list.replaceChildren();
  products.forEach((product, i) => {
    if (!product) return;
    const node = tpl.content.firstElementChild.cloneNode(true);
    const title = String(product.title || '').split(' — ')[0];
    node.querySelectorAll('[data-link]').forEach((a) => (a.href = product.url));
    node.querySelector('[data-title]').textContent = title;
    const img = node.querySelector('[data-image]');
    if (product.featured_image) {
      const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
      img.src = `${src}${src.includes('?') ? '&' : '?'}width=240`;
    } else img.remove();
    const min = product.price_min ?? product.price;
    const max = product.price_max ?? min;
    node.querySelector('[data-price]').textContent =
      min === max ? money.format(min / 100) : `${money.format(min / 100)} – ${money.format(max / 100)}`;
    const remove = node.querySelector('[data-wishlist-remove]');
    remove.setAttribute('aria-label', labelRemove.replace('__TITLE__', title));
    remove.addEventListener('click', () => toggle(handles[i]));
    list.append(node);
  });
  empty.hidden = list.children.length > 0;
}

/** Shared product card HTML (templates/product.card.liquid), cached per handle. */
const cardCache = new Map();
async function fetchCard(handle) {
  if (cardCache.has(handle)) return cardCache.get(handle);
  const p = fetch(`${root}products/${encodeURIComponent(handle)}?view=card`)
    .then((r) => (r.ok ? r.text() : null))
    .then((html) => {
      const trimmed = (html || '').trim();
      // Guard: only accept the bare card view, never a full page.
      return trimmed.includes('group/card') && !/<html|<body/i.test(trimmed) ? trimmed : null;
    })
    .catch(() => null);
  cardCache.set(handle, p);
  return p;
}

/** Favourites page grid: [data-wishlist-page] holds a list and an empty state; each piece uses the shared product card, whose heart removes it. */
async function renderPage() {
  const page = document.querySelector('[data-wishlist-page]');
  const list = page?.querySelector('[data-wishlist-page-list]');
  const empty = page?.querySelector('[data-wishlist-page-empty]');
  if (!page || !list || !empty) return;
  const handles = read();
  page.setAttribute('aria-busy', 'true');
  const cards = await Promise.all(handles.map(fetchCard));
  list.replaceChildren();
  cards.forEach((html) => {
    if (!html) return;
    const li = document.createElement('li');
    li.innerHTML = html;
    list.append(li);
  });
  syncButtons();
  const hasItems = list.children.length > 0;
  list.hidden = !hasItems;
  empty.hidden = hasItems;
  page.setAttribute('aria-busy', 'false');
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest?.('[data-wishlist-toggle]');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();
  toggle(btn.dataset.handle);
  if (btn.getAttribute('aria-pressed') === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    btn.animate([{ scale: 1 }, { scale: 1.35 }, { scale: 1 }], { duration: 400, easing: 'cubic-bezier(0.16,1,0.3,1)' });
  }
});

const drawer = document.getElementById('wishlist-drawer');
if (drawer) new MutationObserver(() => drawer.open && renderDrawer()).observe(drawer, { attributes: true, attributeFilter: ['open'] });
// Re-sync buttons added later (quick view, finish swap, recently viewed) and across tabs.
let syncQueued = false;
new MutationObserver(() => {
  if (syncQueued) return;
  syncQueued = true;
  requestAnimationFrame(() => {
    syncQueued = false;
    syncButtons();
  });
}).observe(document.body, { childList: true, subtree: true });
window.addEventListener('storage', (e) => e.key === KEY && (syncButtons(), renderDrawer(), renderPage()));
syncButtons();
renderPage();
